import React, { useMemo, useState } from 'react';
import { ChevronDown, Sparkles, Check } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { cn } from '../../lib/utils';
import { Badge } from '../ui/badge';
import useStore from './store';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import { useAIModels } from '../../services/ai/aiModelHooks';
import type { AIModelOption, ModelSurface } from '../../services/ai/aiModels.types';

// ─── Brand icons ────────────────────────────────────────────────────────────

const OpenAIIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 4.981 4.18a5.985 5.985 0 0 0-3.998 2.9 6.046 6.046 0 0 0 .743 7.097 5.98 5.98 0 0 0 .51 4.911 6.051 6.051 0 0 0 6.515 2.9A5.985 5.985 0 0 0 13.26 24a6.056 6.056 0 0 0 5.772-4.206 5.99 5.99 0 0 0 3.997-2.9 6.056 6.056 0 0 0-.747-7.073zM13.26 22.43a4.476 4.476 0 0 1-2.876-1.04l.141-.081 4.779-2.758a.795.795 0 0 0 .392-.681v-6.737l2.02 1.168a.071.071 0 0 1 .038.052v5.583a4.504 4.504 0 0 1-4.494 4.494zM3.6 18.304a4.47 4.47 0 0 1-.535-3.014l.142.085 4.783 2.759a.771.771 0 0 0 .78 0l5.843-3.369v2.332a.08.08 0 0 1-.033.062L9.74 19.95a4.5 4.5 0 0 1-6.14-1.646zM2.34 7.896a4.485 4.485 0 0 1 2.366-1.973V11.6a.766.766 0 0 0 .388.676l5.815 3.355-2.02 1.168a.076.076 0 0 1-.071 0l-4.83-2.786A4.504 4.504 0 0 1 2.34 7.872zm16.597 3.855l-5.833-3.387L15.119 7.2a.076.076 0 0 1 .071 0l4.83 2.791a4.494 4.494 0 0 1-.676 8.105v-5.678a.79.79 0 0 0-.407-.667zm2.01-3.023l-.141-.085-4.774-2.782a.776.776 0 0 0-.785 0L9.409 9.23V6.897a.066.066 0 0 1 .028-.061l4.83-2.787a4.5 4.5 0 0 1 6.68 4.66zm-12.64 4.135l-2.02-1.164a.08.08 0 0 1-.038-.057V6.075a4.5 4.5 0 0 1 7.375-3.453l-.142.08L8.704 5.46a.795.795 0 0 0-.393.681zm1.097-2.365l2.602-1.5 2.607 1.5v2.999l-2.597 1.5-2.607-1.5z" />
  </svg>
);

const AnthropicIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M13.827 3.52h3.603L24 20.48h-3.603l-6.57-16.96zm-7.258 0h3.767L16.906 20.48h-3.674l-1.343-3.461H5.017l-1.344 3.46H0l6.57-16.96zm4.132 10.24L8.453 7.687 6.205 13.76h4.496z" />
  </svg>
);

const GoogleIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2 2 19.5h4.2L12 9.3l3.4 6.1H11l-1.8 3.2H22z" />
  </svg>
);

const PROVIDER_META: Record<
  string,
  { label: string; Icon: React.FC<{ className?: string }>; color: string }
> = {
  openai: { label: 'OpenAI', Icon: OpenAIIcon, color: 'text-[#10a37f]' },
  anthropic: { label: 'Anthropic', Icon: AnthropicIcon, color: 'text-[#d97706]' },
  google: { label: 'Google', Icon: GoogleIcon, color: 'text-[#4285f4]' },
  deepseek: { label: 'DeepSeek', Icon: Sparkles, color: 'text-[#4d6bfe]' },
};

const providerMeta = (provider: string) =>
  PROVIDER_META[provider] || { label: provider, Icon: Sparkles, color: 'text-muted-foreground' };

// ─── Props ────────────────────────────────────────────────────────────────--

interface ModelSelectorProps {
  /** Which surface to list models for (background -> light tier only). */
  surface?: ModelSurface;
  /** Controlled selected model key. When omitted, binds to the chat store. */
  value?: string;
  /** Called with the selected model key (controlled mode). */
  onChange?: (modelKey: string) => void;
  /** Override the workspace id (defaults to the active workspace). */
  workspaceId?: string;
  align?: 'start' | 'center' | 'end';
  className?: string;
  disabled?: boolean;
}

// ─── ModelSelector ──────────────────────────────────────────────────────────

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  surface = 'agent',
  value,
  onChange,
  workspaceId: workspaceIdProp,
  align = 'start',
  className,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);

  const storeWorkspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const workspaceId = workspaceIdProp ?? storeWorkspaceId;

  const modelConfig = useStore((s) => s.modelConfig);
  const setModelConfig = useStore((s) => s.setModelConfig);

  const { data, isLoading } = useAIModels({ workspaceId, surface });
  const models: AIModelOption[] = data?.models ?? [];

  // Controlled value wins; otherwise use the shared chat store.
  const selectedKey = value ?? modelConfig.model_key;

  const selectedModel = useMemo(
    () => models.find((m) => m.key === selectedKey),
    [models, selectedKey],
  );

  const triggerLabel =
    selectedModel?.display_name || modelConfig.display_name || selectedKey || 'Select model';

  // Group models by provider, preserving registry order.
  const grouped = useMemo(() => {
    const order: string[] = [];
    const byProvider: Record<string, AIModelOption[]> = {};
    models.forEach((m) => {
      if (!byProvider[m.provider]) {
        byProvider[m.provider] = [];
        order.push(m.provider);
      }
      byProvider[m.provider].push(m);
    });
    return order.map((provider) => ({ provider, items: byProvider[provider] }));
  }, [models]);

  const handleSelect = (model: AIModelOption) => {
    if (onChange) {
      onChange(model.key);
    } else {
      setModelConfig({
        model_key: model.key,
        provider: model.provider,
        display_name: model.display_name,
      });
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50',
            className,
          )}
        >
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span>{triggerLabel}</span>
          <ChevronDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} sideOffset={8} className="w-[300px] p-0">
        <Command>
          <CommandInput placeholder="Search models..." />
          <CommandList>
            <CommandEmpty>{isLoading ? 'Loading models…' : 'No models found.'}</CommandEmpty>
            {grouped.map(({ provider, items }) => {
              const meta = providerMeta(provider);
              return (
                <CommandGroup key={provider} heading={meta.label}>
                  {items.map((model) => {
                    const isSelected = model.key === selectedKey;
                    return (
                      <CommandItem
                        key={model.key}
                        value={`${model.display_name} ${model.provider} ${model.key}`}
                        onSelect={() => handleSelect(model)}
                      >
                        <meta.Icon className={cn('h-4 w-4 shrink-0', meta.color)} />
                        <span className="flex-1 truncate">{model.display_name}</span>
                        {model.tier === 'light' && <Badge variant="secondary">Fast</Badge>}
                        {isSelected && <Check className="h-4 w-4 shrink-0 text-primary" />}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default ModelSelector;
